import { ChangeEvent, useEffect } from 'react';

import useRecommenderStore from '@store/RecommenderStore';
import useRecommenderProfileStore, { SectionName } from '@store/RecommenderProfileStore';

import SectionAccordion from '@components/SectionAccordion';
import YesNoRadioGroupWithClearButton from '@components/YesNoRadioGroupWithClearButton';

import { REQUIRED_FIELD_MESSAGE } from '@constants/texts';

import { SchoolDetailsProps, SchoolDetailsTeacherFields } from '../../types';

import { StyledCard, StyledFieldsContainer } from '../../../Card.styles';

export const SchoolDetails = ({ onExpand, isExtended, isSaved }: SchoolDetailsProps<SchoolDetailsTeacherFields>) => {
  const { recommenderProfile } = useRecommenderStore((store) => store);

  const isShowErrors = new URLSearchParams(window.location.search).get('showErrors') === 'true';

  const {
    initialState: { schoolDetailsTeacher: initialState },
    formState: { schoolDetailsTeacher: formState },
    setInitialData,
    setSectionFormState,
  } = useRecommenderProfileStore((store) => store);

  const initializeData = () => {
    const initialValues = {
      academicRating: recommenderProfile?.teacherProfile?.academicRating,
    };

    setInitialData(SectionName.SchoolDetailsTeacher, initialValues);
  };

  useEffect(() => {
    initializeData();
  }, [recommenderProfile]);

  const updateFormValues = (fieldUpdates: Partial<SchoolDetailsTeacherFields>) => {
    const newFormState = {
      ...formState,
      data: {
        ...formState.data,
        ...fieldUpdates,
      },
    };

    const fields = Object.keys(newFormState.data) as Array<keyof SchoolDetailsTeacherFields>;
    newFormState.hasChanged = fields.some((field) => {
      if (!initialState) {
        return false;
      }

      return newFormState.data[field] !== initialState[field];
    });

    setSectionFormState(SectionName.SchoolDetailsTeacher, newFormState);
  };

  const handleRadioChange = (field: string) => (event: ChangeEvent<HTMLInputElement>) => {
    updateFormValues({ [field]: event.target.value });
  };

  return (
    <SectionAccordion
      title={'School Details'}
      isExpanded={isExtended}
      onChange={onExpand}
      isChanged={formState.hasChanged}
    >
      {Object.keys(formState.data).length === 0 ? null : (
        <StyledCard>
          <StyledFieldsContainer>
            <YesNoRadioGroupWithClearButton
              name="academicRating"
              title="Are you willing to provide applicants' academic ratings on the Common App Teacher evaluation form as a Teacher?"
              value={formState.data.academicRating}
              required
              handleRadioChange={handleRadioChange}
              handleClear={() => updateFormValues({ academicRating: null })}
              error={isShowErrors && !formState.data.academicRating}
              helperText={isShowErrors && !formState.data.academicRating ? REQUIRED_FIELD_MESSAGE : undefined}
            />
          </StyledFieldsContainer>
        </StyledCard>
      )}
    </SectionAccordion>
  );
};
